<template>
  <v-dialog v-model="dialog" max-width="900" persistent scrollable>
    <v-card>
      <v-card-title class="headline">
        {{ $t("STOCK.REVISIONS.LOTS_DIALOG.TITLE") }}
      </v-card-title>
      <v-card-subtitle class="pb-0">
        <span v-if="breakdown && breakdown.product">
          {{ breakdown.product.code }} — {{ breakdown.product.display_name }}
        </span>
      </v-card-subtitle>
      <v-card-text>
        <p class="caption grey--text text--darken-1 mt-2 mb-3">
          {{ $t("STOCK.REVISIONS.LOTS_DIALOG.HINT") }}
        </p>

        <v-alert v-if="!isEditable && breakdown" type="info" dense outlined>
          {{ $t("STOCK.REVISIONS.LOTS_DIALOG.ONLY_LATEST") }}
        </v-alert>

        <v-alert v-if="errorMessage" type="error" dense outlined>
          {{ errorMessage }}
        </v-alert>

        <div class="d-flex flex-wrap mb-2">
          <v-chip small class="mr-2 mb-2" color="grey lighten-3">
            <span class="font-weight-medium mr-1">
              {{ $t("STOCK.REVISIONS.LOTS_DIALOG.TARGET") }}:
            </span>
            {{ formatQuantity(targetQuantity) }} {{ unitCode }}
          </v-chip>
          <v-chip
            small
            class="mr-2 mb-2"
            :color="isBalanced ? 'green lighten-4' : 'red lighten-4'"
          >
            <span class="font-weight-medium mr-1">
              {{ $t("STOCK.REVISIONS.LOTS_DIALOG.ALLOCATED") }}:
            </span>
            {{ formatQuantity(allocatedQuantity) }} {{ unitCode }}
          </v-chip>
        </div>

        <v-simple-table dense>
          <thead>
            <tr>
              <th>{{ $t("STOCK.REVISIONS.LOTS_DIALOG.LOT_NUMBER") }}</th>
              <th>{{ $t("STOCK.REVISIONS.LOTS_DIALOG.PRODUCTION_DATE") }}</th>
              <th>{{ $t("STOCK.REVISIONS.LOTS_DIALOG.EXPIRY_DATE") }}</th>
              <th class="text-right">
                {{ $t("STOCK.REVISIONS.LOTS_DIALOG.SYSTEM_QUANTITY") }}
              </th>
              <th class="text-right" style="min-width: 140px">
                {{ $t("STOCK.REVISIONS.LOTS_DIALOG.COUNTED_QUANTITY") }}
              </th>
              <th v-if="isEditable"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(lot, index) in lots" :key="lot.row_key">
              <td>
                <template v-if="lot.is_new">
                  <v-text-field
                    v-model="lot.lot_number"
                    dense
                    hide-details
                    solo
                    flat
                    :placeholder="$t('STOCK.REVISIONS.LOTS_DIALOG.NEW_LOT')"
                  ></v-text-field>
                </template>
                <template v-else>
                  {{ lot.lot_number || $t("STOCK.REVISIONS.LOTS_DIALOG.NO_LOT") }}
                </template>
              </td>
              <td>
                <DatePicker
                  v-if="lot.is_new"
                  v-model="lot.production_date"
                  without-icon
                  without-hint
                  dense
                  hide-details
                  clearable
                />
                <template v-else>{{ lot.production_date || "-" }}</template>
              </td>
              <td>
                <DatePicker
                  v-if="lot.is_new"
                  v-model="lot.expiry_date"
                  without-icon
                  without-hint
                  dense
                  hide-details
                  clearable
                />
                <template v-else>{{ lot.expiry_date || "-" }}</template>
              </td>
              <td class="text-right">{{ formatQuantity(lot.system_quantity) }}</td>
              <td>
                <v-text-field
                  v-if="isEditable"
                  v-model.number="lot.counted_quantity"
                  type="number"
                  step="0.001"
                  min="0"
                  dense
                  hide-details
                  solo
                  flat
                  class="text-right"
                ></v-text-field>
                <div v-else class="text-right">
                  {{ formatQuantity(lot.counted_quantity) }}
                </div>
              </td>
              <td v-if="isEditable" class="text-center">
                <v-btn icon x-small color="error" @click="removeLot(index)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="!lots.length">
              <td :colspan="isEditable ? 6 : 5" class="text-center grey--text py-4">
                {{ $t("STOCK.REVISIONS.NO_ROWS") }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <div v-if="isEditable" class="mt-2">
          <v-btn small text color="primary" @click="addLot">
            <v-icon left small>mdi-plus</v-icon>
            {{ $t("STOCK.REVISIONS.LOTS_DIALOG.ADD_LOT") }}
          </v-btn>
          <div class="caption grey--text text--darken-1">
            {{ $t("STOCK.REVISIONS.LOTS_DIALOG.NEW_LOT_COST_HINT") }}
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <div
          v-if="isEditable && !isBalanced"
          class="error--text font-weight-medium caption px-2"
        >
          {{
            $t("STOCK.REVISIONS.LOTS_DIALOG.MISMATCH", {
              difference: formatSignedQuantity(differenceQuantity),
            })
          }}
        </div>
        <div v-else-if="isEditable" class="success--text caption px-2">
          {{ $t("STOCK.REVISIONS.LOTS_DIALOG.BALANCED") }}
        </div>
        <v-spacer></v-spacer>
        <v-btn text @click="close">
          {{ $t("GENERAL.LABELS.CLOSE_BUTTON") }}
        </v-btn>
        <v-btn
          v-if="isEditable && isBalanced"
          color="primary"
          :loading="isSaving"
          @click="save"
        >
          {{ $t("GENERAL.LABELS.SAVE") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DatePicker from "@/views/partials/form/DatePicker.vue";
import stockRevisionDisplayMixin from "@/views/partials/stock/revisions/stockRevisionDisplay.mixin";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  GET_STOCK_REVISION_ITEM_LOTS,
  SAVE_STOCK_REVISION_ITEM_LOTS,
} from "@/store/stock/stockRevisions.module";

const EPSILON = 0.0001;

export default {
  name: "StockRevisionLotsDialog",
  components: { DatePicker },
  mixins: [stockRevisionDisplayMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    revisionId: {
      type: [Number, String],
      default: null,
    },
    itemId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      dialog: this.value,
      breakdown: null,
      lots: [],
      isSaving: false,
      errorMessage: "",
      nextRowKey: 1,
    };
  },
  computed: {
    isEditable() {
      return Boolean(this.breakdown && this.breakdown.is_editable);
    },
    unitCode() {
      return (this.breakdown && this.breakdown.product && this.breakdown.product.unit_code) || "";
    },
    targetQuantity() {
      return this.breakdown ? Number(this.breakdown.target_quantity || 0) : 0;
    },
    allocatedQuantity() {
      return this.lots.reduce((total, lot) => total + Number(lot.counted_quantity || 0), 0);
    },
    differenceQuantity() {
      return this.allocatedQuantity - this.targetQuantity;
    },
    isBalanced() {
      return Math.abs(this.differenceQuantity) <= EPSILON;
    },
  },
  watch: {
    value(value) {
      this.dialog = value;
      if (value) {
        this.load();
      }
    },
    dialog(value) {
      this.$emit("input", value);
    },
  },
  methods: {
    async load() {
      this.errorMessage = "";
      this.breakdown = null;
      this.lots = [];

      if (!this.revisionId || !this.itemId) {
        return;
      }

      try {
        const breakdown = await this.$store.dispatch(GET_STOCK_REVISION_ITEM_LOTS, {
          id: this.revisionId,
          itemId: this.itemId,
        });
        this.applyBreakdown(breakdown);
      } catch (error) {
        this.errorMessage = (error && error.message) || this.$t("GENERAL.UNEXPECTED_ERROR");
      }
    },
    applyBreakdown(breakdown) {
      this.breakdown = breakdown;
      this.nextRowKey = 1;
      this.lots = (breakdown.lots || []).map((lot) => ({
        row_key: `lot-${this.nextRowKey++}`,
        stock_lot_id: lot.stock_lot_id,
        lot_number: lot.lot_number,
        production_date: lot.production_date,
        expiry_date: lot.expiry_date,
        system_quantity: Number(lot.system_quantity || 0),
        counted_quantity: Number(lot.counted_quantity || 0),
        is_new: false,
      }));
    },
    addLot() {
      this.lots.push({
        row_key: `new-${this.nextRowKey++}`,
        stock_lot_id: null,
        lot_number: "",
        production_date: null,
        expiry_date: null,
        system_quantity: 0,
        counted_quantity: 0,
        is_new: true,
      });
    },
    removeLot(index) {
      this.lots.splice(index, 1);
    },
    async save() {
      this.errorMessage = "";
      this.isSaving = true;

      const payload = this.lots.map((lot) => ({
        stock_lot_id: lot.stock_lot_id,
        lot_number: lot.lot_number || null,
        counted_quantity: Number(lot.counted_quantity || 0),
        production_date: lot.production_date || null,
        expiry_date: lot.expiry_date || null,
      }));

      try {
        const { breakdown, meta } = await this.$store.dispatch(SAVE_STOCK_REVISION_ITEM_LOTS, {
          id: this.revisionId,
          itemId: this.itemId,
          lots: payload,
        });
        this.applyBreakdown(breakdown);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.REVISIONS.LOTS_DIALOG.SAVED"),
          type: "success",
        });
        this.$emit("saved", { breakdown, meta });
        this.close();
      } catch (error) {
        this.errorMessage =
          (error && error.errors && error.errors.lots && error.errors.lots[0]) ||
          (error && error.message) ||
          this.$t("GENERAL.UNEXPECTED_ERROR");
      } finally {
        this.isSaving = false;
      }
    },
    close() {
      this.dialog = false;
    },
  },
};
</script>
